import { regions } from "@/data/main-data";
import { useGetProperties } from "@/hooks/main-hook";
import { PropertiesInfoProps } from "@/types/main-types";
import { trunkPropertyDesc } from "@/utils/trunck";
import Image from "next/image";
import Link from "next/link";
import { useLocale, useTranslations } from "use-intl";

export default function RelatedProperties({
  id = "",
  tab = "all",
}: {
  id: string;
  tab: string;
}) {
  const filter = {
    page: 1,
    limit: 3,
    propertyId: id,
    tab,
  };

  const t = useTranslations("Properties");
  const locale = useLocale();
  const { data } = useGetProperties(filter);

  const filterProperties = data?.properties || [];

  return (
    <div className="mt-14">
      <div className="flex items-center gap-3 xs:gap-5">
        <h2 className="text-lg sm:text-2xl font-extrabold text-main">
          {t("related")}
        </h2>
        <span className="w-full h-0.5 bg-gray-300 flex-1 rounded"></span>
      </div>

      <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {filterProperties?.map((property: PropertiesInfoProps) => {
          const region = regions.find((e) => e.value === property?.region);
          const isSale = property?.is_sale;

          return (
            <div
              key={property.id}
              className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 group border border-gray-200"
            >
              {/* IMAGE */}
              <div className="overflow-hidden">
                <Image
                  src={property.thumbnail}
                  alt={locale === "ar" ? property.title_ar : property.title_en}
                  width={500}
                  height={350}
                  className="w-full h-80 object-cover group-hover:scale-105 transition-transform duration-500"
                />
              </div>

              {/* CONTENT */}
              <div className="p-5">
                {/* TITLE */}
                <h2 className="text-xl font-extrabold text-gray-800 mb-1">
                  {locale === "ar" ? property.title_ar : property.title_en}
                </h2>

                {/* REGION */}
                <h3 className="text-sm text-gray-500 mb-3">
                  {locale === "ar" ? region?.label_ar : region?.label_en}
                </h3>

                {/* PRICE */}
                <div className="flex items-center gap-1 mb-2">
                  <p className="text-main font-bold text-2xl">
                    {isSale
                      ? property.sale_price?.toLocaleString()
                      : property.rent_type === "yearly"
                      ? property.price_per_year?.toLocaleString()
                      : property.price_per_day?.toLocaleString()}
                  </p>
                  <Image src="/sar.png" alt="SAR" width={15} height={15} />
                  {!property?.is_sale && (
                    <span>/{locale === "ar" ? "سنة" : "year"}</span>
                  )}
                </div>

                {/* DESCRIPTION */}
                <p className="text-gray-600 text-sm leading-relaxed mb-5">
                  {trunkPropertyDesc(
                    locale === "ar"
                      ? property.description_ar
                      : property.description_en,
                    70
                  )}
                </p>

                {/* BUTTON */}
                <Link
                  href={`/properties/${property.slug}`}
                  className="
                block w-full text-center py-3 rounded-xl font-semibold bg-linear-to-r from-main to-main/80 text-white shadow-md hover:opacity-90 transition-all duration-300"
                >
                  {locale === "ar" ? "احجز الآن" : "Book Now"}
                </Link>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
